import { getApiUrl } from "@lib/utils";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

export interface User {
  id: number;
  name: string;
  email: string;
  created_at?: string;
  updated_at?: string;
}

export interface UserListResponse {
  list: User[];
}

export interface UserPaginatedResponse {
  list: User[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export interface UsersQueryParams {
  page?: number;
  page_size?: number;
  search?: string;
  sort_by?: string;
  sort_order?: "asc" | "desc";
}

export interface UserCreatePayload {
  name: string;
  email: string;
  password: string;
}

export interface UserUpdatePayload {
  name?: string;
  email?: string;
  password?: string;
}

function getAuthToken(): string | null {
  return localStorage.getItem("admin_token");
}

// API functions
async function fetchUsers(params?: UsersQueryParams): Promise<UserPaginatedResponse> {
  const token = getAuthToken();
  if (!token) {
    throw new Error("No authentication token found");
  }
  const apiUrl = getApiUrl();
  
  const queryParams = new URLSearchParams();
  queryParams.append("token", token);
  if (params?.page) {
    queryParams.append("page", params.page.toString());
  }
  if (params?.page_size) {
    queryParams.append("page_size", params.page_size.toString());
  }
  if (params?.search) {
    queryParams.append("search", params.search);
  }
  if (params?.sort_by) {
    queryParams.append("sort_by", params.sort_by);
  }
  if (params?.sort_order) {
    queryParams.append("sort_order", params.sort_order);
  }
  
  const response = await fetch(`${apiUrl}/user?${queryParams.toString()}`, {
    headers: {
      "Content-Type": "application/json",
    },
  });
  if (!response.ok) {
    throw new Error("Failed to fetch users");
  }
  return response.json();
}

async function fetchUser(id: number): Promise<User> {
  const token = getAuthToken();
  if (!token) {
    throw new Error("No authentication token found");
  }
  const apiUrl = getApiUrl();
  const response = await fetch(`${apiUrl}/user/${id}?token=${token}`, {
    headers: {
      "Content-Type": "application/json",
    },
  });
  if (!response.ok) {
    throw new Error(`Failed to fetch user with ID ${id}`);
  }
  return response.json();
}

async function createUserApi(payload: UserCreatePayload): Promise<User> {
  const token = getAuthToken();
  if (!token) {
    throw new Error("No authentication token found");
  }
  const apiUrl = getApiUrl();
  const response = await fetch(`${apiUrl}/user?token=${token}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: "Failed to create user" }));
    throw new Error(error.detail || "Failed to create user");
  }
  return response.json();
}

async function updateUserApi({ id, payload }: { id: number; payload: UserUpdatePayload }): Promise<User> {
  const token = getAuthToken();
  if (!token) {
    throw new Error("No authentication token found");
  }
  const apiUrl = getApiUrl();
  const response = await fetch(`${apiUrl}/user/${id}?token=${token}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: "Failed to update user" }));
    throw new Error(error.detail || "Failed to update user");
  }
  return response.json();
}

async function deleteUserApi(id: number): Promise<void> {
  const token = getAuthToken();
  if (!token) {
    throw new Error("No authentication token found");
  }
  const apiUrl = getApiUrl();
  const response = await fetch(`${apiUrl}/user/${id}?token=${token}`, {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
    },
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: "Failed to delete user" }));
    throw new Error(error.detail || "Failed to delete user");
  }
}

// TanStack Query hooks
export function useUsers(params?: UsersQueryParams) {
  return useQuery<UserPaginatedResponse, Error>({
    queryKey: ["users", params],
    queryFn: () => fetchUsers(params),
  });
}

export function useUser(id: number) {
  return useQuery<User, Error>({
    queryKey: ["user", id],
    queryFn: () => fetchUser(id),
    enabled: !!id, // Only run if id is available
  });
}

export function useCreateUser() {
  const queryClient = useQueryClient();
  return useMutation<User, Error, UserCreatePayload>({
    mutationFn: createUserApi,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
    },
  });
}

export function useUpdateUser() {
  const queryClient = useQueryClient();
  return useMutation<User, Error, { id: number; payload: UserUpdatePayload }>({
    mutationFn: updateUserApi,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
      queryClient.invalidateQueries({ queryKey: ["user", variables.id] });
    },
  });
}

export function useDeleteUser() {
  const queryClient = useQueryClient();
  return useMutation<void, Error, number>({
    mutationFn: deleteUserApi,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
    },
  });
}

